import React, { useEffect, useMemo, useState } from 'react'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { getAccountSummary, type SubscriptionSummary } from '../api/account'
import { getCreatorPricing, type CreatorPricingPlan, type StripePlanPrice } from '../api/pricing'

type PlanFeatureValue = 'true' | 'false' | string

type PlanCard = {
  slug: 'free' | 'creator' | 'creator_pro'
  name: string
  tagline: string
  features: string[]
  highlight?: boolean
  price: StripePlanPrice | null
}

const COMMUNITY_PLAN: PlanCard = {
  slug: 'free',
  name: 'Community',
  tagline: 'Read, react, and share across Pixeloop.',
  features: [
    'Read and save content',
    'Comment and react',
    'Share to social',
    'Gift PIXE to creators',
    'Spend PIXE in the community',
  ],
  price: null,
}

const CREATOR_COPY: Record<'creator' | 'creator_pro', Omit<PlanCard, 'slug' | 'price'>> = {
  creator: {
    name: 'Creator',
    tagline: 'Publish, earn PIXE, and unlock USD withdrawals.',
    features: [
      'Publish up to 10 posts per month',
      'Earn PIXE from gifts and subscriptions',
      'Basic creator analytics + payout dashboard',
      'Withdraw available earnings via Stripe',
    ],
    highlight: true,
  },
  creator_pro: {
    name: 'Creator Pro',
    tagline: 'Everything in Creator with higher publishing and faster vesting.',
    features: [
      'Everything in Creator',
      'Unlimited publishing',
      'Faster vesting cadence (6 months)',
      'Higher AI/creator tool limits where enabled',
    ],
  },
}

const COMPARE_ROWS: Array<{ key: string; free: PlanFeatureValue; creator: PlanFeatureValue; creator_pro: PlanFeatureValue }> = [
  { key: 'Publish posts', free: '1 PIXE/post', creator: '10/mo', creator_pro: 'Unlimited' },
  { key: 'Earn PIXE', free: 'Receive & spend', creator: 'true', creator_pro: 'true' },
  { key: 'Withdraw earnings (Stripe)', free: 'false', creator: 'true', creator_pro: 'true' },
  { key: 'Analytics', free: 'false', creator: 'Basic', creator_pro: 'Enhanced limits' },
  { key: 'Pro jobs / service listings', free: 'false', creator: 'Pixe Pros add-on', creator_pro: 'Pixe Pros add-on' },
]

export function formatPriceLabel(price: StripePlanPrice | null): string {
  if (!price) return 'Pricing unavailable'
  if (price.amount_cents == null || !price.currency) return 'Pricing unavailable'
  const usd = (price.amount_cents / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: String(price.currency || 'usd').toUpperCase(),
    maximumFractionDigits: 2,
  })
  return `${usd}/mo`
}

export function mapPricingPlansToCards(creatorPlans: CreatorPricingPlan[]): PlanCard[] {
  const byTier = new Map(creatorPlans.map((p) => [p.tier, p]))
  const creator = byTier.get('creator')
  const creatorPro = byTier.get('creator_pro')
  return [
    COMMUNITY_PLAN,
    {
      slug: 'creator',
      ...CREATOR_COPY.creator,
      price: creator?.prices?.monthly ?? null,
    },
    {
      slug: 'creator_pro',
      ...CREATOR_COPY.creator_pro,
      price: creatorPro?.prices?.monthly ?? null,
    },
  ]
}

function renderFeatureValue(v: PlanFeatureValue) {
  if (v === 'true') return <i className="fa-solid fa-check text-emerald-400" />
  if (v === 'false') return <i className="fa-solid fa-minus text-white/50" />
  return <span className="text-white/80 text-sm">{v}</span>
}

function matchesCurrentPlan(slug: PlanCard['slug'], sub: SubscriptionSummary | null): boolean {
  const current = String(sub?.plan?.slug || '').toLowerCase()
  if (!current) return false
  if (slug === 'creator') return current === 'creator' || current === 'creator-plus'
  if (slug === 'creator_pro') return current === 'creator_pro' || current === 'creator-pro' || current === 'pro'
  return current === 'community' || current === 'free'
}

export function subscribeHref(slug: PlanCard['slug']): string | null {
  if (slug === 'creator') return '/subscribe?tier=creator&interval=month'
  if (slug === 'creator_pro') return '/subscribe?tier=creator_pro&interval=month'
  return null
}

export default function Pricing() {
  const authed = Boolean(getToken())
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null)
  const [pricing, setPricing] = useState<CreatorPricingPlan[]>([])
  const [pricingLoading, setPricingLoading] = useState(true)
  const [pricingError, setPricingError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setPricingLoading(true)
    setPricingError(null)
    getCreatorPricing()
      .then((payload) => {
        if (cancelled) return
        setPricing(payload?.plans || [])
      })
      .catch(() => {
        if (cancelled) return
        setPricing([])
        setPricingError('Pricing unavailable')
      })
      .finally(() => {
        if (!cancelled) setPricingLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!authed) return
    let cancelled = false
    getAccountSummary()
      .then((s) => {
        if (!cancelled) setSubscription(s.subscription)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [authed])

  const planCards = useMemo(() => mapPricingPlansToCards(pricing), [pricing])

  return (
    <div className="min-h-screen">
      <NavBar />

      <main className="mx-auto max-w-6xl px-4 py-10">
        <section className="text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">Simple, transparent pricing</h1>
          <p className="mt-3 text-white/80 text-lg">PIXE powers creation. Stripe handles subscriptions and USD withdrawals.</p>
          {pricingError ? <div className="mt-3 text-sm text-amber-300">{pricingError}</div> : null}
        </section>

        <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {pricingLoading
            ? [0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-5 animate-pulse h-64" />
              ))
            : planCards.map((plan) => {
                const isCurrent = matchesCurrentPlan(plan.slug, subscription)
                return (
                  <div key={plan.slug} className={`rounded-2xl border ${plan.highlight ? 'border-brand' : 'border-white/10'} bg-white/5 p-5 flex flex-col`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div>
                        <div className="text-xl font-extrabold">{plan.name}</div>
                        <div className="text-white/80 text-sm">{plan.tagline}</div>
                      </div>
                      {isCurrent ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
                          Current
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <div className="text-3xl font-extrabold">
                        {plan.slug === 'free' ? 'Free' : formatPriceLabel(plan.price)}
                      </div>
                    </div>
                    <ul className="mt-4 space-y-2 text-white/90">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="fa-solid fa-check text-emerald-400 mt-1" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {plan.slug !== 'free' ? (
                      <div className="mt-6">
                        {authed ? (
                          isCurrent ? (
                            <a href="/settings/billing" className="inline-flex items-center justify-center w-full rounded-xl bg-brand px-4 py-2 font-semibold hover:bg-brand-600">
                              Manage Billing
                            </a>
                          ) : subscribeHref(plan.slug) ? (
                            <a href={subscribeHref(plan.slug) || '#'} className="inline-flex items-center justify-center w-full rounded-xl bg-brand px-4 py-2 font-semibold hover:bg-brand-600">
                              {plan.slug === 'creator' ? 'Become Creator' : 'Become Creator Pro'}
                            </a>
                          ) : null
                        ) : (
                          <a href="/login" className="inline-flex items-center justify-center w-full rounded-xl bg-brand px-4 py-2 font-semibold hover:bg-brand-600">
                            Get Started
                          </a>
                        )}
                      </div>
                    ) : null}
                  </div>
                )
              })}
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-extrabold mb-4">Compare plans</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-separate border-spacing-y-2">
              <thead className="text-white/80">
                <tr>
                  <th className="px-3 py-2">Feature</th>
                  <th className="px-3 py-2">Community</th>
                  <th className="px-3 py-2">Creator</th>
                  <th className="px-3 py-2">Creator Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row) => (
                  <tr key={row.key} className="bg-white/5">
                    <td className="px-3 py-2 font-semibold text-white/90">{row.key}</td>
                    <td className="px-3 py-2">{renderFeatureValue(row.free)}</td>
                    <td className="px-3 py-2">{renderFeatureValue(row.creator)}</td>
                    <td className="px-3 py-2">{renderFeatureValue(row.creator_pro)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-xs text-white/60">
            Pro jobs and service listings run through Pixe Pros onboarding and escrow workflows.
          </div>
        </section>
      </main>
    </div>
  )
}
